import React from 'react';
import { Box, Chip, Skeleton, Typography } from '@mui/material';
import zeplinColor from 'theme/Color';
import ClientAvatar from 'components/shared/ClientAvatar';
import DealDropdown, {
  OwnerOption,
} from 'components/featured/Deals/components/DealDetailsMain/DealDropdown';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { useOwnerOptions } from 'components/shared/hooks/useOwnerOptions';
import { useEffect, useMemo, useState } from 'react';
import { HuddleClientService } from 'service/huddle-client.service';

type ProfileDetailsTabProps = {
  details: any;
  loading: boolean;
  accordionExpanded?: boolean;
};

const FieldItem = ({
  label,
  value,
  loading,
  isChip = false,
}: {
  label: string;
  value: React.ReactNode;
  loading: boolean;
  isChip?: boolean;
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    {loading ? (
      <Skeleton variant='text' width='80%' height={18} />
    ) : isChip ? (
      <Chip
        label={
          <Typography variant='responsiveLabel1' color={zeplinColor.BasicBlack}>
            {value?.toLocaleString() || 'N/A'}
          </Typography>
        }
        sx={{
          backgroundColor: zeplinColor.Neutral4,
          width: 'fit-content',
          padding: '4px 8px',
          borderRadius: '50px',
          height: '24px',
        }}
      />
    ) : (
      <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
        {value?.toLocaleString() || 'N/A'}
      </Typography>
    )}
    <Typography sx={{ fontWeight: 500, fontSize: '14px', color: zeplinColor.Neutral8 }}>
      {label}
    </Typography>
  </Box>
);

const AvatarField = ({
  label,
  name,
  imageUrl,
  loading,
}: {
  label: string;
  name?: string;
  imageUrl?: string;
  loading: boolean;
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    {loading ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Skeleton variant='circular' width={24} height={24} />
        <Skeleton variant='text' width={80} height={18} />
      </Box>
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ClientAvatar
          profilePicture={imageUrl}
          clientName={name}
          sx={{ width: '24px', height: '24px', fontSize: '10px' }}
        />
        <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
          {name || 'N/A'}
        </Typography>
      </Box>
    )}
    <Typography sx={{ fontWeight: 500, fontSize: '14px', color: zeplinColor.Neutral8 }}>
      {label}
    </Typography>
  </Box>
);

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box
    sx={{
      backgroundColor: zeplinColor.Neutral2,
      borderRadius: '14px',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    }}
  >
    <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
      {title}
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '20px',
      }}
    >
      {children}
    </Box>
  </Box>
);

const formatAddress = (addr: any): string => {
  if (!addr) return 'N/A';
  const parts = [addr.street, addr.city, addr.state, addr.postalCode, addr.country].filter(Boolean);
  return parts.length ? parts.join(', ') : 'N/A';
};

const ProfileDetailsTab = ({
  details,
  loading,
  accordionExpanded = false,
}: ProfileDetailsTabProps) => {
  const { ownerOptions } = useOwnerOptions();
  const [selectedOwner, setSelectedOwner] = useState<OwnerOption | null>(null);
  const { startOperation, alertState, setAlertState, getAlertContent, completeOperation } =
    useAlertStatus();

  const clientId = useMemo(
    () => details?.clientId || details?.accountId || details?.Id || details?.id,
    [details],
  );

  const ownerDropdownOptions = useMemo(
    () =>
      ownerOptions.map((owner) => ({
        cognitoSub: owner.Id,
        fullName: owner.Name,
      })),
    [ownerOptions],
  );

  useEffect(() => {
    if (!details?.ownerName && !details?.ownerProfileImageUrl && !details?.owner) {
      setSelectedOwner(null);
      return;
    }

    const matchedOwner = ownerDropdownOptions.find(
      (owner) => owner.cognitoSub === details?.owner || owner.fullName === details?.ownerName,
    );

    setSelectedOwner(
      matchedOwner || {
        cognitoSub: details?.owner || '',
        fullName: details?.ownerName || details?.owner || 'N/A',
        profilePicture: details?.ownerProfileImageUrl,
      },
    );
  }, [details, ownerDropdownOptions]);

  const handleOwnerChange = async (_fieldName: string, value: OwnerOption | null) => {
    if (!value || !clientId) return;

    const previousOwner = selectedOwner;
    setSelectedOwner(value);

    try {
      startOperation('update', 'Updating owner...');
      await HuddleClientService.editHuddleClient({ owner: value.cognitoSub }, clientId);
      completeOperation('update', 'Owner updated successfully.');
    } catch (error: any) {
      setSelectedOwner(previousOwner || null);
      completeOperation('update', error?.response?.data?.message || 'Failed to update owner', true);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        padding: { xs: '16px 0', xl: '20px 0' },
        overflowY: 'auto',
        height: accordionExpanded
          ? { xs: 'calc(100vh - 460px)', xl: 'calc(100vh - 510px)' }
          : { xs: 'calc(100vh - 320px)', xl: 'calc(100vh - 370px)' },
      }}
    >
      {/* Account Information */}
      <SectionCard title='Account Information'>
        <FieldItem label='Agency' value={details?.agency} loading={loading} />
        <FieldItem label='League' value={details?.league} loading={loading} isChip />
        <FieldItem label='Team' value={details?.team} loading={loading} />
        <FieldItem label='Position' value={details?.position} loading={loading} />
        <FieldItem label='Number' value={details?.number} loading={loading} />
        <FieldItem
          label='Status'
          value={details?.status ?? details?.accountStatus}
          loading={loading}
        />
        <FieldItem label='College' value={details?.college} loading={loading} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Skeleton variant='circular' width={24} height={24} />
              <Skeleton variant='text' width={80} height={18} />
            </Box>
          ) : (
            <DealDropdown
              options={ownerDropdownOptions}
              value={selectedOwner}
              setValue={handleOwnerChange}
              fieldName='ownerName'
              isBgDark={false}
            />
          )}
          <Typography sx={{ fontWeight: 500, fontSize: '14px', color: zeplinColor.Neutral8 }}>
            Owner
          </Typography>
        </Box>
      </SectionCard>

      {/* Contact Information */}
      <SectionCard title='Contact Information'>
        <FieldItem label='Mobile' value={details?.personMobilePhone} loading={loading} />
        <FieldItem label='Phone' value={details?.phone} loading={loading} />
        <FieldItem label='Email' value={details?.email} loading={loading} />
        <FieldItem label='Personal Email' value={details?.personEmail} loading={loading} />
        <FieldItem
          label='Mailing Address'
          value={formatAddress(details?.personMailingAddress)}
          loading={loading}
        />
        <FieldItem
          label='Billing Address'
          value={formatAddress(details?.billingAddress)}
          loading={loading}
        />
      </SectionCard>

      {/* Additional Information */}
      <SectionCard title='Additional Information'>
        <FieldItem label='Loan Out Company' value={details?.loanOutCompany} loading={loading} />
        <FieldItem
          label='Client Summary'
          value={details?.clientSummary ?? details?.bio}
          loading={loading}
        />
        <AvatarField
          label='Created By'
          name={details?.createdByName ?? details?.createdBy}
          imageUrl={details?.createdByProfileImageUrl}
          loading={loading}
        />
      </SectionCard>

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};

export default ProfileDetailsTab;
